import React, { useState, useEffect } from 'react';
import useValveStore from '../../stores/useValveStore';
import styles from './ToolCard.module.css';

const ValveToolCard = () => {
  const valveState = useValveStore((state) => state.valve);
  const setValve = useValveStore((state) => state.setValve);
  
  // Use local state for UI but sync with store
  const [valveSettings, setValveSettings] = useState(valveState);
  const [enabledInputFocused, setEnabledInputFocused] = useState(false);
  const [disabledInputFocused, setDisabledInputFocused] = useState(false);

  // Sync with store when component mounts or store changes
  useEffect(() => {
    setValveSettings(valveState);
  }, [valveState]);

  const isEnabled = valveSettings.enabled;
  const enabledValue = valveSettings.enabledValue || 8.0;
  const disabledValue = valveSettings.disabledValue || 8.0;

  const handleEnabledChange = (enabled) => {
    setValveSettings(prev => ({ ...prev, enabled }));
    setValve({ enabled });
  };

  const handleEnabledInputChange = (value) => {
    const numValue = parseFloat(value) || 0;
    const clampedValue = Math.max(0, Math.min(100, numValue));
    const roundedValue = Math.round(clampedValue * 10) / 10;
    setValveSettings(prev => ({ ...prev, enabledValue: roundedValue }));
    setValve({ enabledValue: roundedValue });
  };

  const handleDisabledInputChange = (value) => {
    const numValue = parseFloat(value) || 0;
    const clampedValue = Math.max(0, Math.min(100, numValue));
    const roundedValue = Math.round(clampedValue * 10) / 10;
    setValveSettings(prev => ({ ...prev, disabledValue: roundedValue }));
    setValve({ disabledValue: roundedValue });
  };

  return (
    <div className={styles.toolCard}>
      <div className={styles.toolCardHeader}>
        <h3>Valve</h3>
      </div>
      
      <div style={{ 
        border: '2px solid #7c3aed', 
        borderRadius: 8, 
        padding: 16, 
        background: 'white',
        marginBottom: 16
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Enabled Option with Input Field */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                id="enabled"
                name="valve"
                checked={isEnabled}
                onChange={() => handleEnabledChange(true)}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: '#7c3aed'
                }}
              />
              <label htmlFor="enabled" style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Enabled
              </label>
            </div>
            
            {/* Input Field for Enabled */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              borderRadius: 6,
              padding: '2px 8px',
              background: 'white'
            }}>
              <input
                type="number"
                step="0.1"
                value={Math.round(enabledValue * 10) / 10}
                onChange={(e) => handleEnabledInputChange(e.target.value)}
                onFocus={() => setEnabledInputFocused(true)}
                onBlur={() => setEnabledInputFocused(false)}
                disabled={!isEnabled}
                style={{
                  width: 60,
                  padding: 3,
                  border: enabledInputFocused ? '1px solid #7c3aed' : '1px solid #d1d5db',
                  borderRadius: 3,
                  fontSize: 12,
                  textAlign: 'center',
                  background: isEnabled ? 'transparent' : '#f3f4f6',
                  outline: 'none',
                  fontWeight: 600,
                  color: isEnabled ? '#374151' : '#9ca3af',
                  boxShadow: enabledInputFocused ? '0 0 0 2px rgba(124, 58, 237, 0.1)' : 'none',
                  cursor: isEnabled ? 'text' : 'not-allowed'
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 600, color: isEnabled ? '#374151' : '#9ca3af' }}>mm</span>
            </div>
          </div>

          {/* Separator */}
          <div style={{ 
            height: 1, 
            backgroundColor: '#e5e7eb', 
            margin: '4px 0' 
          }} />

          {/* Disabled Option with Input Field */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                id="disabled"
                name="valve"
                checked={!isEnabled}
                onChange={() => handleEnabledChange(false)}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: '#7c3aed'
                }}
              />
              <label htmlFor="disabled" style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Disabled
              </label>
            </div>
            
            {/* Input Field for Disabled */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              borderRadius: 6,
              padding: '2px 8px',
              background: 'white'
            }}>
              <input
                type="number"
                step="0.1"
                value={Math.round(disabledValue * 10) / 10}
                onChange={(e) => handleDisabledInputChange(e.target.value)}
                onFocus={() => setDisabledInputFocused(true)}
                onBlur={() => setDisabledInputFocused(false)}
                disabled={isEnabled}
                style={{
                  width: 60,
                  padding: 3,
                  border: disabledInputFocused ? '1px solid #7c3aed' : '1px solid #d1d5db',
                  borderRadius: 3,
                  fontSize: 12,
                  textAlign: 'center',
                  background: !isEnabled ? 'transparent' : '#f3f4f6',
                  outline: 'none',
                  fontWeight: 600,
                  color: !isEnabled ? '#374151' : '#9ca3af',
                  boxShadow: disabledInputFocused ? '0 0 0 2px rgba(124, 58, 237, 0.1)' : 'none',
                  cursor: !isEnabled ? 'text' : 'not-allowed'
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 600, color: !isEnabled ? '#374151' : '#9ca3af' }}>mm</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValveToolCard; 